import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Question {
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation?: string;
  bloomsLevel?: string;
  standardReference?: string;
}

interface PassageData {
  title: string;
  passage: string;
  genre: string;
  questions: Question[];
  gradeLevel: string;
}

interface StreamingPassageProps {
  passages: PassageData[];
  combinedQuestions?: Question[];
  answerKey?: string;
  isLoading: boolean;
  isPairedPassage: boolean;
  showAnswerKey?: boolean;
}

const StreamingPassage: React.FC<StreamingPassageProps> = ({
  passages,
  combinedQuestions,
  answerKey,
  isLoading,
  isPairedPassage,
  showAnswerKey = true,
}) => {
  const renderQuestions = (questions: Question[], sectionTitle: string) => (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {sectionTitle}
      </Typography>
      {questions.map((question, qIndex) => (
        <Box key={qIndex} sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {qIndex + 1}. {question.question}
          </Typography>
          {question.answers.map((answer, aIndex) => (
            <Typography key={aIndex} variant="body1" sx={{ ml: 3, mb: 0.5 }}>
              {String.fromCharCode(65 + aIndex)}. {answer}
            </Typography>
          ))}
          {showAnswerKey && (
            <Box sx={{ mt: 1, ml: 3, color: 'text.secondary' }}>
              <Typography variant="body2" color="primary">
                Correct Answer: {question.correctAnswer}
              </Typography>
              {question.explanation && (
                <Typography variant="body2">
                  Explanation: {question.explanation}
                </Typography>
              )}
              {question.standardReference && (
                <Typography variant="body2">
                  Standard: {question.standardReference}
                </Typography>
              )}
              {question.bloomsLevel && (
                <Typography variant="body2">
                  Bloom's Level: {question.bloomsLevel}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      {passages.map((passage, index) => (
        <Box key={index}>
          <Typography variant="h5" gutterBottom>
            {isPairedPassage ? `Passage ${index + 1}: ${passage.title}` : passage.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Genre: {passage.genre} • Grade Level: {passage.gradeLevel}
          </Typography>
          <Box sx={{ my: 3 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {passage.passage}
            </ReactMarkdown>
          </Box>
          
          {passage.questions.length > 0 && renderQuestions(
            passage.questions,
            isPairedPassage ? `Questions for Passage ${index + 1}` : 'Questions'
          )}
          
          {index < passages.length - 1 && <Divider sx={{ my: 4 }} />}
        </Box>
      ))}

      {isPairedPassage && combinedQuestions && combinedQuestions.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          {renderQuestions(combinedQuestions, 'Questions About Both Passages')}
        </>
      )}

      {showAnswerKey && answerKey && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Complete Answer Key
            </Typography>
            <Typography
              variant="body1"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                ml: 2
              }}
            >
              {answerKey}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default StreamingPassage; 